import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { GetRatingQuery } from '@generated/graphql'

import type {
  Product,
  ProductItem,
  SkuSpecification,
} from './types/product.type'
import { getInitialItem } from './utils/get-initial-sku'
import type { NotificationMessage } from './types/index.type'

type Location = {
  search: string
  pathname: string
}

type ProductProviderProps = {
  location: Location
  product: Product
  productData: any
  children: JSX.Element | JSX.Element[]
  rating: GetRatingQuery['getRating']
}

type SpecificationNotSelect = SkuSpecification | null | false

type ProductContextProps = {
  product: Product
  productData: any
  initialSku: ProductItem
  currentSku: ProductItem
  setCurrentSku: Dispatch<SetStateAction<ProductItem>>
  specificationNotSelected: SpecificationNotSelect
  setSpecificationNotSelected: Dispatch<SetStateAction<SpecificationNotSelect>>
  notification: NotificationMessage | null
  setNotification: Dispatch<SetStateAction<NotificationMessage | null>>
  rating: GetRatingQuery['getRating']
}

export const ProductContext = createContext<ProductContextProps>(
  {} as ProductContextProps
)

export const ProductProvider = ({
  children,
  product,
  productData,
  location,
  rating,
}: ProductProviderProps) => {
  const [specificationNotSelected, setSpecificationNotSelected] =
    useState<SpecificationNotSelect>(false)

  const [notification, setNotification] = useState<NotificationMessage | null>(
    null
  )

  const initialSku = getInitialItem(product.items, location)
  const [currentSku, setCurrentSku] = useState(initialSku)

  useEffect(() => {
    setCurrentSku(getInitialItem(product.items, location))
  }, [product, location])

  useEffect(() => {
    setNotification(null)
  }, [specificationNotSelected])

  const productContextValue = useMemo(
    () => ({
      initialSku,
      product,
      currentSku,
      setCurrentSku,
      productData,
      specificationNotSelected,
      setSpecificationNotSelected,
      notification,
      setNotification,
      rating,
    }),
    [
      initialSku,
      product,
      currentSku,
      productData,
      specificationNotSelected,
      notification,
      rating,
    ]
  )

  return (
    <ProductContext.Provider value={productContextValue}>
      {children}
    </ProductContext.Provider>
  )
}

export const useProductPageContext = () => useContext(ProductContext)
